import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  Clock,
  MessageSquare,
  ClipboardCheck,
  ScrollText,
  Megaphone,
  Users,
  UserPlus,
  MapPin,
  Sun,
  Moon,
  LogOut,
  ShieldCheck,
  ShieldAlert,
  Building2,
  ChevronDown,
  ChevronsUpDown,
  Wrench,
  CalendarClock,
  CalendarRange,
  AlertOctagon,
  KeyRound,
  FileText,
  Camera,
  Trash2,
  Menu,
  X,
  UserX,
  FolderOpen,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useJobsite } from "../../context/JobsiteContext";
import { useTheme } from "../../context/ThemeContext";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import ImageCropper from "../ImageCropper";
import { authenticateUser, markAsked, setEnabledUser, useBiometric } from "../../lib/biometric";

const workerNav = [
  { to: "/app", label: "Dashboard", icon: LayoutGrid, end: true },
  { to: "/app/attendance", label: "Attendance", icon: Clock },
  { to: "/app/my-shifts", label: "My Shifts", icon: CalendarRange },
  { to: "/app/chat", label: "Chat", icon: MessageSquare },
  { to: "/app/inspections", label: "Inspections", icon: ClipboardCheck },
  { to: "/app/logs", label: "Daily Logs", icon: ScrollText },
  { to: "/app/photos", label: "Documents", icon: FolderOpen },
  { to: "/app/announcements", label: "Announcements", icon: Megaphone },
  { to: "/app/forms", label: "Forms", icon: FileText },
];

const adminNav = [
  { to: "/app/admin", label: "Admin Console", icon: ShieldCheck, end: true },
  { to: "/app/admin/users", label: "Users & Access", icon: Users },
  { to: "/app/admin/employees", label: "Manage Employees", icon: UserPlus },
  { to: "/app/admin/jobsites", label: "Jobsites", icon: MapPin },
  { to: "/app/admin/roster", label: "Roster & Shifts", icon: CalendarRange },
  { to: "/app/admin/time-tracking", label: "Time Tracking", icon: CalendarClock },
  { to: "/app/admin/chat-monitor", label: "Chat Monitor", icon: MessageSquare },
  { to: "/app/admin/itp-templates", label: "ITP Templates", icon: Wrench },
  { to: "/app/admin/forms", label: "Forms", icon: FileText },
  { to: "/app/admin/ncrs", label: "NCRs", icon: AlertOctagon },
  { to: "/app/admin/company", label: "Company Settings", icon: Building2 },
  { to: "/app/change-password", label: "Change Password", icon: KeyRound },
];

// Abas fixas embaixo no celular (a quinta, "More", abre o menu completo).
const tabNav = [
  { to: "/app", label: "Home", icon: LayoutGrid, end: true },
  { to: "/app/attendance", label: "Time", icon: Clock },
  { to: "/app/inspections", label: "Inspect", icon: ClipboardCheck },
  { to: "/app/chat", label: "Chat", icon: MessageSquare },
];

const masterNav = [
  { to: "/app/master", label: "Company Approvals", icon: ShieldAlert },
];

// A foto de perfil e salva no backend como arquivo; o usuario guarda so o link
// curto (ex: "/files/avatars/abc.jpg"). Precisa completar com o endereco do backend.
const BACKEND_ORIGIN = "https://webapp-sitesync.onrender.com";
const resolveAvatarUrl = (url) => (url && url.startsWith("/") ? `${BACKEND_ORIGIN}${url}` : url);
// Limite da foto escolhida (a foto enquadrada que vai pro servidor fica bem menor).
const AVATAR_PICK_MAX_BYTES = 30 * 1024 * 1024;

// Fonte do app: Inter. Injeta o link do Google Fonts uma unica vez.
function useProfessionalFont() {
  useEffect(() => {
    const id = "sitesync-font-inter";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);
}

export default function AppShell({ children }) {
  const { user, logout, refresh } = useAuth();
  const { sites, activeSite, selectSite } = useJobsite();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin" || user?.role === "supervisor";
  const isMaster = !!user?.is_master_admin;

  useProfessionalFont();

  // Face ID: ligar e desligar pelo menu (so aparece no app do iPhone, se o aparelho tiver Face ID ou Touch ID)
  const bio = useBiometric();
  const bioOn = !!user && bio.enabledUser === user.id;

  const avatarInputRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  // Foto escolhida, esperando o enquadramento (tela de recortar).
  const [cropSrc, setCropSrc] = useState(null);

  // Menu de celular (gaveta lateral)
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Exclusao da propria conta (exigencia da App Store)
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // Fecha a gaveta sempre que a pessoa troca de pagina.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Trava a rolagem da pagina de fundo enquanto a gaveta esta aberta.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const toggleBiometric = async () => {
    if (!user) return;
    if (bioOn) {
      setEnabledUser("");
      toast.success(`${bio.label} turned off`);
      return;
    }
    const res = await authenticateUser(`Turn on ${bio.label} for SiteSync`);
    if (res.ok) {
      setEnabledUser(user.id);
      markAsked(user.id);
      toast.success(`${bio.label} is on`);
    } else if (!res.cancelled) {
      toast.error(res.message);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleAvatarFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > AVATAR_PICK_MAX_BYTES) {
      toast.error("Image is too large (max 30 MB).");
      return;
    }
    setCropSrc(URL.createObjectURL(file));
  };

  const closeCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const uploadCropped = async (blob) => {
    closeCrop();
    setAvatarBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      await api.post("/auth/me/avatar", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload the photo.");
    } finally {
      setAvatarBusy(false);
    }
  };

  const handleAvatarRemove = async () => {
    setAvatarBusy(true);
    try {
      await api.delete("/auth/me/avatar");
      await refresh();
      toast.success("Photo removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove the photo.");
    } finally {
      setAvatarBusy(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteOpen(false);
    setDeletePassword("");
    setDeleteError("");
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (!deletePassword) {
      setDeleteError("Enter your password to confirm.");
      return;
    }
    setDeleteBusy(true);
    setDeleteError("");
    try {
      await api.post("/auth/delete-account", { password: deletePassword });
      setEnabledUser("");
      toast.success("Your account was deleted");
      await logout();
      navigate("/login");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setDeleteError(typeof detail === "string" ? detail : "Could not delete the account. Please try again.");
      setDeleteBusy(false);
    }
  };

  // Conteudo do menu lateral. Usado no desktop (fixo) e no celular (gaveta).
  const renderSidebar = (onNavigate) => (
    <>
      <div className="h-16 shrink-0 flex items-center px-4 border-b border-border">
        <span className="text-xl font-bold tracking-tight">SiteSync</span>
      </div>

      <div className="shrink-0 border-b border-border p-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="user-menu-trigger"
              className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-accent transition-colors text-left"
            >
              {user?.avatar_url ? (
                <img
                  src={resolveAvatarUrl(user.avatar_url)}
                  alt={user?.name || "User"}
                  className="h-11 w-11 rounded-full object-cover shrink-0 bg-muted"
                  data-testid="user-avatar-img"
                />
              ) : (
                <div className="h-11 w-11 rounded-full bg-primary/15 text-primary grid place-items-center font-semibold text-sm uppercase shrink-0">
                  {(user?.name || "?").slice(0, 2)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate leading-tight">{user?.name}</div>
                <div className="label-overline mt-0.5 truncate">{user?.role}</div>
              </div>
              <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="bottom" className="w-56 rounded-sm">
            <DropdownMenuLabel className="font-mono uppercase tracking-widest text-[10px]">
              {user?.name}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => avatarInputRef.current?.click()}
              disabled={avatarBusy}
              className="rounded-sm gap-2"
              data-testid="change-photo-btn"
            >
              <Camera className="h-4 w-4" /> {avatarBusy ? "Uploading..." : user?.avatar_url ? "Change photo" : "Add photo"}
            </DropdownMenuItem>
            {user?.avatar_url && (
              <DropdownMenuItem onClick={handleAvatarRemove} disabled={avatarBusy} className="rounded-sm gap-2">
                <Trash2 className="h-4 w-4" /> Remove photo
              </DropdownMenuItem>
            )}
            {bio.supported && (
              <DropdownMenuItem onClick={toggleBiometric} className="rounded-sm gap-2" data-testid="toggle-biometric">
                <ShieldCheck className="h-4 w-4" /> {bioOn ? `Turn off ${bio.label}` : `Turn on ${bio.label}`}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => navigate("/app/change-password")} className="rounded-sm gap-2">
              <KeyRound className="h-4 w-4" /> Change Password
            </DropdownMenuItem>
            {!isMaster && (
              <DropdownMenuItem
                onClick={() => setDeleteOpen(true)}
                data-testid="delete-account-btn"
                className="rounded-sm gap-2 text-destructive focus:text-destructive"
              >
                <UserX className="h-4 w-4" /> Delete account
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              data-testid="logout-btn"
              className="rounded-sm gap-2 text-destructive focus:text-destructive"
            >
              <LogOut className="h-4 w-4" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <nav className="flex-1 px-2 py-4 space-y-5 overflow-y-auto">
        {isMaster ? (
          <NavGroup label="Master Admin" items={masterNav} onNavigate={onNavigate} />
        ) : (
          <>
            <NavGroup label="Workspace" items={workerNav} onNavigate={onNavigate} />
            {isAdmin && <NavGroup label="Administration" items={adminNav} onNavigate={onNavigate} />}
          </>
        )}
      </nav>
    </>
  );

  return (
    <div
      className="min-h-screen flex bg-background text-foreground"
      style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif" }}
    >
      {/* Campo de arquivo da foto de perfil: um so, fora dos menus */}
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleAvatarFile}
        data-testid="avatar-file-input"
      />

      {/* Menu lateral fixo (computador) */}
      <aside className="w-60 shrink-0 border-r border-border bg-card hidden md:flex md:flex-col">
        {renderSidebar()}
      </aside>

      {/* Gaveta lateral (celular) */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-drawer">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-black/50"
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col border-r border-border bg-card"
            style={{
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setDrawerOpen(false)}
              className="absolute right-2 z-10 rounded-md p-2 text-muted-foreground hover:bg-accent"
              style={{ top: "calc(env(safe-area-inset-top) + 0.5rem)" }}
            >
              <X className="h-5 w-5" />
            </button>
            {renderSidebar(() => setDrawerOpen(false))}
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header
          className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-40 flex items-center gap-3 px-4 md:px-6"
          style={{
            paddingTop: "env(safe-area-inset-top)",
            height: "calc(4rem + env(safe-area-inset-top))",
          }}
          data-testid="app-header"
        >
          <Button
            variant="ghost"
            size="icon"
            className={cn("shrink-0", isMaster ? "md:hidden" : "hidden")}
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            data-testid="open-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="rounded-sm border-border h-11 md:h-9 gap-2 min-w-0"
                data-testid="jobsite-selector-dropdown"
              >
                <Building2 className="h-4 w-4 shrink-0" />
                <span className="font-mono text-xs uppercase tracking-wider truncate max-w-[140px] md:max-w-[160px]">
                  {activeSite?.name || "Select Jobsite"}
                </span>
                <ChevronDown className="h-3 w-3 opacity-60 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 max-w-[90vw] rounded-sm">
              <DropdownMenuLabel className="font-mono uppercase tracking-widest text-[10px]">
                Jobsites
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {sites.length === 0 && (
                <div className="px-2 py-3 text-xs text-muted-foreground">No jobsites yet.</div>
              )}
              {sites.map((s) => (
                <DropdownMenuItem
                  key={s.id}
                  onClick={() => selectSite(s.id)}
                  data-testid={`jobsite-option-${s.id}`}
                  className="rounded-sm"
                >
                  <div className="flex flex-col">
                    <span className="text-sm">{s.name}</span>
                    <span className="text-[11px] text-muted-foreground font-mono">
                      {s.address || "No address"}
                    </span>
                  </div>
                </DropdownMenuItem>
              ))}
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate("/app/admin/jobsites")}
                    data-testid="manage-jobsites-btn"
                  >
                    Manage jobsites
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex-1" />

          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11 md:h-10 md:w-10"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </header>

        <main
          className={cn(
            "ss-main flex-1 overflow-y-auto",
            isMaster ? "pb-[env(safe-area-inset-bottom)]" : "pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0",
          )}
        >
          {children}
        </main>
      </div>

      {/* Barra de abas do celular: icones grandes, faceis de tocar com o dedo */}
      {!isMaster && (
        <nav
          className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 backdrop-blur md:hidden"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          aria-label="Main"
          data-testid="mobile-tabbar"
        >
          <div className="grid grid-cols-5">
            {tabNav.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={`tab-${t.label.toLowerCase()}`}
                className={({ isActive }) =>
                  cn(
                    "relative flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors active:bg-accent",
                    isActive ? "text-primary" : "text-muted-foreground",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute inset-x-4 top-0 h-0.5 rounded-b bg-primary" />}
                    <t.icon className="h-6 w-6" strokeWidth={isActive ? 2.4 : 2} />
                    {t.label}
                  </>
                )}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium text-muted-foreground active:bg-accent"
              data-testid="tab-more"
            >
              <Menu className="h-6 w-6" />
              More
            </button>
          </div>
        </nav>
      )}

      {/* Enquadrar a foto de perfil antes de enviar */}
      {cropSrc && (
        <ImageCropper
          src={cropSrc}
          shape="round"
          aspect={1}
          outputWidth={512}
          outputType="image/jpeg"
          title="Profile photo"
          onCancel={closeCrop}
          onDone={uploadCropped}
        />
      )}

      {/* Confirmacao de exclusao de conta */}
      {deleteOpen && (
        <Dialog open onOpenChange={(o) => !o && !deleteBusy && closeDeleteDialog()}>
          <DialogContent className="rounded-sm max-w-md">
            <form onSubmit={handleDeleteAccount}>
              <DialogHeader>
                <DialogTitle>Delete your account?</DialogTitle>
                <DialogDescription>
                  This permanently removes your name, email, phone, address and photo, and you will no longer be
                  able to sign in. Work records of your company (attendance, inspections, logs) are kept without
                  identifying you. This cannot be undone.
                </DialogDescription>
              </DialogHeader>

              <div className="py-4 space-y-2">
                <label htmlFor="delete-account-password" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                  Confirm with your password
                </label>
                <Input
                  id="delete-account-password"
                  type="password"
                  autoComplete="current-password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="rounded-sm"
                  data-testid="delete-account-password"
                />
                {deleteError && (
                  <div className="text-sm text-destructive" data-testid="delete-account-error">
                    {deleteError}
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={closeDeleteDialog} disabled={deleteBusy}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={deleteBusy}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid="delete-account-confirm"
                >
                  {deleteBusy ? "Deleting..." : "Delete my account"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function NavGroup({ label, items, onNavigate }) {
  return (
    <div>
      <div className="label-overline px-3 mb-2">{label}</div>
      <div className="space-y-0.5">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={onNavigate}
            data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 px-3 py-3 md:py-2 rounded-md text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-foreground/80 hover:bg-accent hover:text-foreground",
              )
            }
          >
            <it.icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{it.label}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}
